import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Skeleton,
  SvgIconProps,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { useTranslation } from "react-i18next";
import { useSessionStatus } from "@/hooks/useTroubleshooting";
import {
  GeofenceSyncDto,
  GeofenceSyncState,
} from "@/domain/troubleshooting.interface";
import MapIcon from "@mui/icons-material/Map";
import { formatInBackofficeLondon } from "@/utils/dateUtils";
import { TIMELINE_TIME_FORMAT } from "./TimelineSwimlane";
import {
  buildSyncMapModel,
  DEFAULT_PARTICIPANT_TIMELINE_CONFIG,
  formatAge,
} from "@/utils/troubleshooting.utils";
import TroubleshootingMap from "./TroubleshootingMap";

/** Badge per sync state: colour plus a distinct icon (not colour alone). */
export const GEOFENCE_SYNC_STATE_META: Record<
  GeofenceSyncState,
  {
    color: "success" | "error" | "warning" | "default";
    Icon: React.ComponentType<SvgIconProps>;
  }
> = {
  OK: { color: "success", Icon: CheckCircleIcon },
  MISCONFIGURED: { color: "error", Icon: ErrorOutlineIcon },
  NOT_STARTED: { color: "warning", Icon: PauseCircleOutlineIcon },
  UNKNOWN: { color: "default", Icon: HelpOutlineIcon },
};

const fmt = (iso?: string) =>
  iso ? formatInBackofficeLondon(iso, TIMELINE_TIME_FORMAT) : "-";

const meters = (value?: number) =>
  value === undefined || value === null ? "-" : `${value} m`;

const Item: React.FC<{
  label: string;
  children: React.ReactNode;
  testId?: string;
}> = ({ label, children, testId }) => (
  <Box sx={{ minWidth: 200 }} data-testid={testId}>
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ fontWeight: 600, display: "block" }}
    >
      {label}
    </Typography>
    <Typography variant="body2" component="div">
      {children}
    </Typography>
  </Box>
);

/**
 * COYN-1507 §11.3.5 / §11.4: was geofence sync enabled correctly on the
 * participant's device, with the program configuration applied? Shares the
 * session-status query with SessionStatusCard (one request).
 */
const GeofenceSyncCard: React.FC<{
  participantId: string;
  /**
   * Window of `deviceEventsNotReceived7d` (the setting's
   * `deviceEventsNotReceivedDays`; the DTO name is kept for compatibility).
   */
  notReceivedDays?: number;
}> = ({
  participantId,
  notReceivedDays = DEFAULT_PARTICIPANT_TIMELINE_CONFIG.deviceEventsNotReceivedDays,
}) => {
  const { t } = useTranslation("troubleshooting");
  const tt = t as (key: string, options?: Record<string, unknown>) => string;
  const {
    data: status,
    isLoading,
    isError,
    refetch,
  } = useSessionStatus(participantId);
  const sync = status?.geofenceSync;
  const meta = sync
    ? GEOFENCE_SYNC_STATE_META[sync.state] || GEOFENCE_SYNC_STATE_META.UNKNOWN
    : null;
  const radiusMismatch =
    sync?.programRadius !== undefined &&
    sync?.programRadius !== null &&
    sync?.appliedRadius !== undefined &&
    sync?.appliedRadius !== null &&
    sync.programRadius !== sync.appliedRadius;

  return (
    <Card
      variant="outlined"
      sx={{ height: "100%" }}
      data-testid="geofence-sync-card"
    >
      <CardContent>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexWrap: "wrap",
            mb: 1,
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {t("geofenceSync.title")}
          </Typography>
          {sync && meta && (
            <Chip
              color={meta.color}
              icon={<meta.Icon aria-hidden />}
              label={tt(`geofenceSync.state.${sync.state}`, {
                defaultValue: sync.state,
              })}
              data-testid="geofence-sync-badge"
              data-state={sync.state}
              sx={{ fontWeight: 700 }}
            />
          )}
          {sync?.reasonCode && (
            <Chip
              size="small"
              variant="outlined"
              title={sync.reasonCode}
              data-reason-code={sync.reasonCode}
              label={tt(`geofenceSync.reasonCodes.${sync.reasonCode}`, {
                defaultValue: sync.reasonCode,
              })}
            />
          )}
        </Box>

        {isLoading && (
          <Box aria-label={t("geofenceSync.loading")}>
            <Skeleton variant="text" width="60%" />
            <Skeleton variant="text" width="80%" />
          </Box>
        )}

        {isError && !status && (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={() => refetch()}>
                {t("timeline.retry")}
              </Button>
            }
          >
            {t("geofenceSync.error")}
          </Alert>
        )}

        {status && !sync && (
          <Typography
            variant="body2"
            color="text.secondary"
            data-testid="geofence-sync-unavailable"
          >
            {t("geofenceSync.unavailable")}
          </Typography>
        )}

        {sync && (
          <>
            <Typography
              variant="body1"
              sx={{ mb: 1.5 }}
              data-testid="geofence-sync-reason"
            >
              {sync.reason || t("geofenceSync.noReason")}
            </Typography>

            {sync.sdkEnrollmentMatchesCurrent === false && (
              <Alert
                severity="warning"
                sx={{ mb: 1 }}
                data-testid="geofence-sync-stale-enrollment"
              >
                {t("geofenceSync.staleEnrollment")}
              </Alert>
            )}
            {sync.deviceEventsNotReceived7d > 0 && (
              <Alert
                severity="warning"
                sx={{ mb: 1 }}
                data-testid="geofence-sync-not-received"
              >
                {tt("geofenceSync.notReceivedWarning", {
                  total: sync.deviceEventsNotReceived7d,
                  days: notReceivedDays,
                })}
              </Alert>
            )}

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mt: 1 }}>
              <Item
                label={t("geofenceSync.fields.lastConfigApplied")}
                testId="geofence-sync-last-config"
              >
                {fmt(sync.lastConfigAppliedAt)}
                {sync.lastConfigOk !== undefined &&
                  sync.lastConfigOk !== null && (
                    <Box
                      component="span"
                      sx={{
                        fontWeight: 600,
                        color: sync.lastConfigOk
                          ? "success.main"
                          : "error.main",
                      }}
                    >
                      {` (${
                        sync.lastConfigOk
                          ? t("geofenceSync.configOk")
                          : t("geofenceSync.configFailed")
                      })`}
                    </Box>
                  )}
              </Item>
              <Item
                label={t("geofenceSync.fields.radius")}
                testId="geofence-sync-radius"
              >
                <Box
                  component="span"
                  data-mismatch={radiusMismatch || undefined}
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 1,
                    flexWrap: "wrap",
                    ...(radiusMismatch && {
                      color: "error.main",
                      fontWeight: 700,
                    }),
                  }}
                >
                  {tt("geofenceSync.radiusValues", {
                    program: meters(sync.programRadius),
                    applied: meters(sync.appliedRadius),
                  })}
                  {radiusMismatch && (
                    <Chip
                      size="small"
                      color="error"
                      icon={<ErrorOutlineIcon aria-hidden />}
                      label={t("geofenceSync.mismatch")}
                    />
                  )}
                </Box>
              </Item>
              <Item label={t("geofenceSync.fields.geofencesRegistered")}>
                {sync.geofencesRegistered ?? "-"}
              </Item>
              <Item label={t("geofenceSync.fields.lastDeviceEvent")}>
                {fmt(sync.lastDeviceEventAt)}
              </Item>
              <Item label={t("geofenceSync.fields.lastBackendEvent")}>
                {fmt(sync.lastBackendEventAt)}
              </Item>
              <Item
                label={tt("geofenceSync.fields.notReceived7d", {
                  days: notReceivedDays,
                })}
                testId="geofence-sync-not-received-count"
              >
                {sync.deviceEventsNotReceived7d}
              </Item>
              <Item
                label={t("geofenceSync.fields.lastStartBlocked")}
                testId="geofence-sync-start-blocked"
              >
                {sync.lastStartBlockedReason ? (
                  <Box component="span" sx={{ fontFamily: "monospace" }}>
                    {sync.lastStartBlockedReason}
                  </Box>
                ) : (
                  "-"
                )}
              </Item>
            </Box>

            <LocationDetails sync={sync} />
          </>
        )}
      </CardContent>
    </Card>
  );
};

const coordinates = (lat?: number, lng?: number) =>
  typeof lat === "number" && typeof lng === "number"
    ? `${lat.toFixed(6)}, ${lng.toFixed(6)}`
    : null;

/**
 * COYN-1507 §17.3 / §17.4: where the device is watching vs the registered
 * addresses, its last position and connection, with an optional map. Every
 * block is skipped when an older backend does not send it.
 */
const LocationDetails: React.FC<{ sync: GeofenceSyncDto }> = ({ sync }) => {
  const { t } = useTranslation("troubleshooting");
  const tt = t as (key: string, options?: Record<string, unknown>) => string;
  const [showMap, setShowMap] = useState(false);
  const which = (key: "primary" | "secondary") => tt(`location.${key}`);
  const mapModel = useMemo(
    () =>
      buildSyncMapModel(sync, {
        primaryAddress: tt("location.map.primaryAddress"),
        secondaryAddress: tt("location.map.secondaryAddress"),
        sdkPrimary: tt("location.map.sdkPrimary"),
        sdkSecondary: tt("location.map.sdkSecondary"),
        lastPosition: tt("location.map.lastPosition"),
      }),
    [sync, tt],
  );
  const addresses = sync.registeredAddresses;
  const centers = sync.sdkCenters;
  const last = sync.lastPosition;
  const connection = sync.connection;
  const offsets = (["primary", "secondary"] as const).filter(
    (key) => typeof sync.centerOffsetMeters?.[key] === "number",
  );

  const hasAny =
    addresses || centers || last || connection || mapModel !== null;
  if (!hasAny && sync.sdkCentersMatchAddress === undefined) return null;

  return (
    <Box sx={{ mt: 2 }} data-testid="geofence-sync-location">
      {sync.sdkCentersMatchAddress === false && (
        <Alert
          severity="error"
          sx={{ mb: 1.5 }}
          data-testid="geofence-sync-center-mismatch"
        >
          {offsets.length > 0
            ? offsets.map((key) => (
                <Box key={key}>
                  {tt("location.centerMismatch", {
                    offset: Math.round(sync.centerOffsetMeters?.[key] ?? 0),
                    which: which(key).toLowerCase(),
                  })}
                </Box>
              ))
            : t("location.centerMismatchUnknown")}
        </Alert>
      )}
      {sync.sdkCentersMatchAddress === true && (
        <Typography
          variant="body2"
          color="success.main"
          sx={{ mb: 1 }}
          data-testid="geofence-sync-center-match"
        >
          {t("location.centersMatch")}
        </Typography>
      )}

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
        {addresses && (
          <Item
            label={t("location.registeredAddresses")}
            testId="geofence-sync-addresses"
          >
            {(["primary", "secondary"] as const)
              .filter((key) => addresses[key])
              .map((key) => {
                const address = addresses[key];
                return (
                  <Box key={key} data-testid={`geofence-sync-address-${key}`}>
                    <strong>{which(key)}:</strong> {address?.line || "-"}
                    {" · "}
                    <Box component="span" sx={{ fontFamily: "monospace" }}>
                      {coordinates(address?.latitude, address?.longitude) ||
                        t("location.noCoordinates")}
                    </Box>
                  </Box>
                );
              })}
          </Item>
        )}

        {centers && (
          <Item
            label={t("location.sdkCenters")}
            testId="geofence-sync-sdk-centers"
          >
            {(["primary", "secondary"] as const)
              .filter((key) => centers[key])
              .map((key) => (
                <Box key={key}>
                  <strong>{which(key)}:</strong>{" "}
                  <Box component="span" sx={{ fontFamily: "monospace" }}>
                    {coordinates(
                      centers[key]?.latitude,
                      centers[key]?.longitude,
                    )}
                  </Box>
                </Box>
              ))}
            {typeof centers.radius === "number" && (
              <Box>{tt("location.radius", { radius: centers.radius })}</Box>
            )}
            {centers.at && (
              <Box sx={{ color: "text.secondary" }}>
                {tt("location.registeredAt", {
                  time: formatInBackofficeLondon(
                    centers.at,
                    TIMELINE_TIME_FORMAT,
                  ),
                })}
              </Box>
            )}
          </Item>
        )}

        {last && (
          <Item
            label={t("location.lastPosition")}
            testId="geofence-sync-last-position"
          >
            <Box component="span" sx={{ fontFamily: "monospace" }}>
              {coordinates(last.latitude, last.longitude)}
            </Box>
            {typeof last.accuracy === "number" && (
              <Box component="span">
                {" "}
                {tt("location.accuracy", {
                  accuracy: Math.round(last.accuracy),
                })}
              </Box>
            )}
            <Box>
              {tt("location.age", {
                age: formatAge(last.at),
                time: formatInBackofficeLondon(last.at, TIMELINE_TIME_FORMAT),
              })}
            </Box>
            {last.insideGeofence !== undefined && (
              <Box
                sx={{
                  fontWeight: 600,
                  color: last.insideGeofence ? "success.main" : "text.primary",
                }}
              >
                {last.insideGeofence
                  ? last.currentGeofence === "primary" ||
                    last.currentGeofence === "secondary"
                    ? tt("location.insideWhich", {
                        which: which(last.currentGeofence).toLowerCase(),
                      })
                    : t("location.inside")
                  : t("location.outside")}
              </Box>
            )}
            {typeof last.distanceToCenterMeters === "number" && (
              <Box>
                {tt("location.distance", {
                  distance: Math.round(last.distanceToCenterMeters),
                })}
              </Box>
            )}
          </Item>
        )}

        {connection && (
          <Item
            label={t("location.connection")}
            testId="geofence-sync-connection"
          >
            <Box>
              {t("location.connectionType")}: {connection.type || "-"}
            </Box>
            {connection.internetReachable !== undefined && (
              <Box
                sx={{
                  color: connection.internetReachable
                    ? "text.primary"
                    : "error.main",
                }}
              >
                {t("location.internet")}:{" "}
                {connection.internetReachable
                  ? t("location.yes")
                  : t("location.no")}
              </Box>
            )}
            {typeof connection.apiLatencyMedianMs === "number" && (
              <Box>
                {t("location.apiLatency")}:{" "}
                {tt("location.latencyValue", {
                  ms: Math.round(connection.apiLatencyMedianMs),
                })}
              </Box>
            )}
          </Item>
        )}
      </Box>

      {mapModel && (
        <Box sx={{ mt: 1.5 }}>
          <Button
            size="small"
            startIcon={<MapIcon />}
            onClick={() => setShowMap((v) => !v)}
            aria-expanded={showMap}
            data-testid="geofence-sync-map-toggle"
          >
            {showMap ? t("location.hideMap") : t("location.showMap")}
          </Button>
          <Collapse in={showMap} unmountOnExit>
            <Box sx={{ mt: 1 }} data-testid="geofence-sync-map">
              <TroubleshootingMap
                model={mapModel}
                labels={{
                  primary: tt("location.map.primaryAddress"),
                  secondary: tt("location.map.secondaryAddress"),
                  event: mapModel.event.label,
                  radius: tt("location.map.radius"),
                  openInGoogleMaps: tt("location.map.openInGoogleMaps"),
                }}
              />
            </Box>
          </Collapse>
        </Box>
      )}
    </Box>
  );
};

export default GeofenceSyncCard;
